import { Info } from 'lucide-react';
import type { Block } from '@auditwire/contracts';
import { cn } from '@/shared/lib/utils/cn';
import { renderInlineMarkdown } from '@/shared/lib/markdown/inline';

/** Renders a single article body block in its reading-view style. */
export function BlockRenderer({ block }: { block: Block }) {
  switch (block.type) {
    case 'h2':
      return <h2 className="font-display text-3xl font-bold text-foreground">{block.content}</h2>;
    case 'h3':
      return <h3 className="font-display text-xl font-bold text-foreground">{block.content}</h3>;
    case 'quote':
      return (
        <blockquote className="border-l-[3px] border-amber-400 pl-7">
          <p className="font-display text-2xl font-bold italic leading-snug text-foreground sm:text-3xl">
            {renderInlineMarkdown(block.content)}
          </p>
        </blockquote>
      );
    case 'callout':
      return (
        <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/40 dark:bg-amber-950/20">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            {renderInlineMarkdown(block.content)}
          </p>
        </div>
      );
    case 'image':
      return block.url ? (
        <figure>
          <img
            src={block.url}
            alt={block.caption ?? ''}
            loading="lazy"
            className="max-h-[32rem] w-full rounded-2xl object-cover"
          />
          {block.caption ? (
            <figcaption className="mt-2 text-center text-xs text-muted-foreground">
              {block.caption}
            </figcaption>
          ) : null}
        </figure>
      ) : null;
    case 'split': {
      // Text beside an image, stacking to a single column on small screens. The
      // image is ordered second in the DOM so it reads after the text when stacked,
      // regardless of which side it takes on wider viewports.
      const imageLeft = block.imagePosition === 'left';
      return (
        <div className="grid items-center gap-6 sm:grid-cols-2">
          <div className={cn('text-[1.0625rem] leading-[1.85] text-foreground', imageLeft && 'sm:order-2')}>
            {renderInlineMarkdown(block.content)}
          </div>
          {block.url ? (
            <figure className={cn(imageLeft && 'sm:order-1')}>
              <img
                src={block.url}
                alt={block.caption ?? ''}
                loading="lazy"
                className="max-h-80 w-full rounded-2xl object-cover"
              />
              {block.caption ? (
                <figcaption className="mt-2 text-center text-xs text-muted-foreground">
                  {block.caption}
                </figcaption>
              ) : null}
            </figure>
          ) : (
            <div
              aria-hidden
              className={cn(
                'flex h-40 items-center justify-center rounded-2xl border border-dashed border-border text-xs text-muted-foreground',
                imageLeft && 'sm:order-1',
              )}
            >
              No image yet
            </div>
          )}
        </div>
      );
    }
    case 'divider':
      return <hr className="border-border" />;
    case 'paragraph':
    default:
      return (
        <p className="text-[1.0625rem] leading-[1.85] text-foreground">
          {renderInlineMarkdown(block.content)}
        </p>
      );
  }
}
